import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useVerifyMagicLink } from "../../../hooks/useAPI";
import { AuthShell, TokenStatePage, type TokenPageStatus } from "./_primitives";

const REDIRECT_DELAY_MS = 1500;

export function MagicLinkPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const verifyMagicLink = useVerifyMagicLink();
  const [status, setStatus] = useState<TokenPageStatus>(token ? "loading" : "no-token");

  useEffect(() => {
    if (!token) return;
    let mounted = true;
    if (typeof window !== "undefined") {
      window.history.replaceState({}, "", window.location.pathname);
    }
    verifyMagicLink.mutate(
      { token },
      {
        onSuccess: () => { if (mounted) setStatus("success"); },
        onError: (err) => {
          if (!mounted) return;
          const code = (err as { code?: string } | undefined)?.code || "";
          const message = err instanceof Error ? err.message : "";
          const lowered = (code + " " + message).toLowerCase();
          if (lowered.includes("expired")) setStatus("expired");
          else if (lowered.includes("already") || code === "TOKEN_USED") setStatus("consumed");
          else setStatus("error");
        },
      },
    );
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (status !== "success") return;
    const id = window.setTimeout(() => navigate("/dashboard"), REDIRECT_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [status, navigate]);

  return (
    <AuthShell pageTitle={t("magicLink.pageTitle")} pageDescription={t("magicLink.pageDescription")}>
      <TokenStatePage
        status={status}
        loadingTitle={t("magicLink.loadingTitle")}
        loadingBody={t("magicLink.loadingBody")}
        successTitle={t("magicLink.successTitle")}
        successBody={t("magicLink.successBody")}
        successAction={
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="adp-btn adp-btn-primary w-full inline-flex items-center justify-center gap-2"
          >
            {t("magicLink.successAction")}
          </button>
        }
        expiredTitle={t("magicLink.expiredTitle")}
        expiredBody={t("magicLink.expiredBody")}
        expiredAction={
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="adp-btn adp-btn-primary w-full inline-flex items-center justify-center gap-2"
          >
            {t("magicLink.backToSignIn")}
          </button>
        }
        consumedTitle={t("magicLink.consumedTitle")}
        consumedBody={t("magicLink.consumedBody")}
        consumedAction={
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="adp-btn adp-btn-primary w-full inline-flex items-center justify-center gap-2"
          >
            {t("magicLink.backToSignIn")}
          </button>
        }
        errorTitle={t("magicLink.errorTitle")}
        errorBody={t("magicLink.errorBody")}
        errorAction={
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="adp-btn adp-btn-primary w-full inline-flex items-center justify-center gap-2"
          >
            {t("magicLink.backToSignIn")}
          </button>
        }
        noTokenTitle={t("magicLink.noTokenTitle")}
        noTokenBody={t("magicLink.noTokenBody")}
        noTokenAction={
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="adp-btn adp-btn-primary w-full inline-flex items-center justify-center gap-2"
          >
            {t("magicLink.backToSignIn")}
          </button>
        }
      />
    </AuthShell>
  );
}
